import { capitalize } from '../../../../../util/text';
import { formatPriceForDisplay } from '../../../../../util/format-money';
import { CURRENCY_SYMBOLS } from '../../../../../constants/currency';

/**
 * Search subscription plan enum.
 *
 * @enum SEARCH_SUBSCRIPTION_PLAN
 * @type {{SHUTTERBUG_PLUS: string, SHUTTERBUG_PREMIUM: string}}
 */
export const SEARCH_SUBSCRIPTION_PLAN = {
  SHUTTERBUG_PLUS: 'SHUTTERBUG_PLUS',
  SHUTTERBUG_PREMIUM: 'SHUTTERBUG_PREMIUM',
};

export const SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD = {
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
};

/**
 * Hardcoded metadata  per SearchSubscription plan.
 */
export const searchSubscriptionMeta = {
  [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS]: {
    accentColor: '#f1794e',
    icon: '/diamond-orange.svg',
    colors: ['#c7e2ff', '#54c9f2', '#3385ed'],
    label: 'Waldo Plus',
    shortLabel: 'Plus',
    features: [
      'Unlimited event galleries',
      'Unlimited photo matching for up to 5 family members',
      'Unlimited invites and contributors',
      '100GB storage',
    ],
    isPreferred: false,
  },
  [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM]: {
    accentColor: '#0d84fe',
    icon: '/diamond-blue.svg',
    colors: ['#ffdaf5', '#e956c7', '#407be9'],
    label: 'Waldo Premium',
    shortLabel: 'Premium',
    features: [
      'Unlimited event galleries',
      'Unlimited photo matching for up to 10 family members',
      'Unlimited invites and contributors',
      '200GB storage',
      '15% off all prints',
    ],
    isPreferred: true,
  },
};

/**
 * Payment processor enum.
 *
 * @enum PAYMENT_PROCESSOR
 * @type {{APP_STORE: string, STRIPE: string, FREE: string}}
 */
export const PAYMENT_PROCESSOR = {
  APP_STORE: 'APP_STORE',
  FREE: 'FREE',
  STRIPE: 'STRIPE',
};

/**
 * Returns a title and label for a SearchSubscriptionItem based on its plan.
 *
 * @param {Object} searchSubscriptionItem The SearchSubscriptionItem.
 * @returns {{label: *, title: *}|{label: string, title: string}}
 */
export const getSearchSubscriptionItemLabels = searchSubscriptionItem => {
  if (!searchSubscriptionItem || !searchSubscriptionItem.plan) {
    return {
      title: '',
      label: '',
    };
  }

  const [title, label] = searchSubscriptionItem.plan.split('_').reverse();

  return {
    title: capitalize(title),
    label: capitalize(label),
  };
};

/**
 * Returns a price label for a SearchSubscriptionItem, ie "$2.99 / year".
 *
 * @param {Object} searchSubscriptionItem The SearchSubscriptionItem.
 * @returns {string}
 */
export const getSearchSubscriptionItemPriceLabel = searchSubscriptionItem => {
  if (!searchSubscriptionItem) {
    return '';
  }

  const price = formatPriceForDisplay(searchSubscriptionItem.price.value);
  const currency = CURRENCY_SYMBOLS[searchSubscriptionItem.price.currency];

  return `${currency}${price}`;
};

/**
 * Checks if an account is eligible for a free trial on the Shuttebrug subscription offerings.
 *
 * @param {Object} account The Account.
 * @returns {boolean}
 */
export const isEligibleForShutterbugFreeTrial = account => {
  return !!account?.search_subscription_free_trial_enabled;
};

/**
 * Determines whether the SearchSubscription is active trial.
 *
 * @param {Object} searchSubscription The SearchSubscription.
 * @returns {boolean}
 */
export const isSearchSubscriptionActiveTrial = searchSubscription => {
  if (!searchSubscription) {
    return false;
  }

  return searchSubscription.trial_days > 0;
};
